import React, { useMemo } from "react";

/**
 * BrandFilterChips — pill-shaped chip row for one-tap brand filtering
 * of a dispensary menu. The leading "All" chip resets the filter; the
 * next chips are the shop's PA-featured brands (Modern Flower,
 * Cultivar, Roll One, Momenta, Khalifa Kush) in that fixed order.
 * Anything past those is populated by whatever other brands actually
 * sit in the menu, ordered by SKU count so the biggest suppliers are
 * one tap away.
 *
 * Filters the menu client-side (controlled component) — the parent
 * (`DispensaryDetail`) stores the selected brand in the same
 * `filters.brand` field the DispensaryMenuFilters dropdown already
 * writes to, so the chips and the dropdown stay in sync.
 */

const FEATURED_BRANDS_ORDER = [
  "Modern Flower",
  "Cultivar",
  "R.O.",
  "Roll One",
  "Momenta",
  "Khalifa Kush",
];

const _brandKey = (b) => (b || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

export function BrandFilterChips({ menu = [], value = "", onChange }) {
  const chips = useMemo(() => {
    // Count SKUs per brand (in-stock takes visual priority — a brand
    // with 0 in-stock chips would be a dead-end tap for shoppers).
    const counts = new Map();
    for (const p of menu) {
      const b = (p.brand || "").trim();
      if (!b) continue;
      counts.set(b, (counts.get(b) || 0) + 1);
    }
    if (counts.size === 0) return [];

    const seen = new Set();
    const ordered = [];

    // Featured brands first — only include those that actually exist
    // on this shop's menu (so a shop that doesn't carry Cultivar
    // doesn't get a dead Cultivar chip).
    for (const name of FEATURED_BRANDS_ORDER) {
      if (counts.has(name)) {
        ordered.push({ name, count: counts.get(name), featured: true });
        seen.add(name);
      }
    }

    // Remaining brands, ordered by SKU count desc (top suppliers first),
    // capped so the chip row doesn't turn into a 30-brand wall on mobile.
    const others = Array.from(counts.entries())
      .filter(([b]) => !seen.has(b))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count, featured: false }));

    return [...ordered, ...others];
  }, [menu]);

  if (chips.length === 0) return null;

  const isActive = (name) => _brandKey(value) === _brandKey(name);

  return (
    <div
      className="mb-6 -mx-4 sm:mx-0"
      data-testid="brand-filter-chips"
    >
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 sm:px-0 pb-1">
        <button
          type="button"
          onClick={() => onChange && onChange("")}
          data-testid="brand-chip-all"
          className={`shrink-0 h-9 rounded-full px-4 text-[12px] uppercase tracking-[0.14em] font-medium transition-colors border ${
            value === ""
              ? "bg-emerald-500 text-emerald-950 border-emerald-500"
              : "bg-card text-foreground/80 border-border hover:border-emerald-500/40 hover:text-emerald-200"
          }`}
        >
          All
        </button>
        {chips.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => onChange && onChange(c.name)}
            data-testid={`brand-chip-${_brandKey(c.name)}`}
            aria-pressed={isActive(c.name)}
            className={`shrink-0 h-9 rounded-full px-4 text-[12px] font-medium transition-colors border inline-flex items-center gap-1.5 ${
              isActive(c.name)
                ? c.featured
                  ? "bg-[#d4a268] text-[#1a1006] border-[#d4a268]"
                  : "bg-emerald-500 text-emerald-950 border-emerald-500"
                : c.featured
                  ? "bg-card text-foreground border-[#d4a268]/40 hover:border-[#d4a268] hover:text-[#d4a268]"
                  : "bg-card text-foreground/80 border-border hover:border-emerald-500/40 hover:text-emerald-200"
            }`}
          >
            {c.name}
            <span
              className={`text-[10px] ${
                isActive(c.name)
                  ? c.featured
                    ? "text-[#1a1006]/70"
                    : "text-emerald-950/70"
                  : "text-foreground/45"
              }`}
            >
              {c.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
